import { useEffect, useMemo } from "react";
import {
  MdAir,
  MdAutoAwesome,
  MdCategory,
  MdCleaningServices,
  MdDirectionsCar,
  MdInventory2,
  MdLocalCarWash,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toGetProductCategoriesTree } from "../../apis/actions/vehicle.actions";
import { AppDispatch, RootState } from "../../apis/store";
import { normalizeServiceTree } from "../../utils/serviceCategoryTree";
import ShopCategoryFilter, {
  type ShopFilterCategory,
} from "./ShopCategoryFilter";

type Props = {
  selectedId: string | null;
  onSelect: (categoryId: string | null) => void;
};

const iconForCategory = (category: ShopFilterCategory | null) => {
  if (!category) return <MdCategory />;

  const normalized = category.name.toLowerCase();
  if (normalized.includes("air fresh")) return <MdAir />;
  if (normalized.includes("clean")) return <MdCleaningServices />;
  if (normalized.includes("spray")) return <MdLocalCarWash />;
  if (normalized.includes("wax")) return <MdAutoAwesome />;
  if (normalized.includes("kahn")) return <MdDirectionsCar />;
  if (normalized.includes("accessor")) return <MdInventory2 />;
  return <MdCategory />;
};

const ProductCategoryFilter = ({ selectedId, onSelect }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { productCategoryTree } = useSelector(
    (state: RootState) => state.vehicle
  );

  useEffect(() => {
    dispatch(toGetProductCategoriesTree({ lang: i18n.language }));
  }, [dispatch, i18n.language]);

  const categories = useMemo(
    () => normalizeServiceTree(productCategoryTree?.list),
    [productCategoryTree?.list]
  );

  return (
    <ShopCategoryFilter
      categories={categories as ShopFilterCategory[]}
      isLoading={
        productCategoryTree?.status === "loading" ||
        productCategoryTree?.status === "idle"
      }
      hasFailed={productCategoryTree?.status === "rejected"}
      selectedId={selectedId}
      onSelect={onSelect}
      eyebrow={t("services.productFilter.eyebrow")}
      title={t("services.productFilter.title")}
      allLabel={t("services.productFilter.all")}
      renderIcon={iconForCategory}
    />
  );
};

export default ProductCategoryFilter;
